import { useState } from 'react'
import { Link, useLocation as useRoute } from 'react-router'
import { SECTIONS, contactCovers, contactLabel, emptyField, emptySchedule, newId, slugify, type ContactsSection, type LocationsSection as Doc, type PortalConfig, type Schedule } from '@sonder/shared'
import { useDoc } from '@/data/hooks'
import { useClientView, useMode } from '@/app/mode'
import { useSectionEditor } from '@/data/editor'
import { reviewInfo, useNameOf } from '@/data/review'
import { CopyToDialog, Field, Input, ReviewBar, ReviewTag, ScheduleBuilder, Select, Toggle, useConfirm, useToast } from '@/ui'
import { AddRow, EntityCard, NoEntities, useBrands } from './common'
import type { ContactsNavState } from './contactHelpers'
import { FieldEditor } from './FieldEditor'

const DEF = SECTIONS.locations
const F = Object.fromEntries(DEF.fields.map((f) => [f.key, f]))

export function LocationsSection({ clientId }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'locations')
  const mode = useMode()
  const clientView = useClientView()
  const nameOf = useNameOf()
  const confirm = useConfirm()
  const brands = useBrands(clientId)
  const [newName, setNewName] = useState('')
  const [newBrand, setNewBrand] = useState('')
  const [copying, setCopying] = useState<{ from: string; schedule: Schedule } | null>(null)
  const contacts = useDoc<ContactsSection>(`clients/${clientId}/sections/contacts`)
  const route = useRoute()
  const contactsHref = route.pathname.replace(/\/onboarding\/[^/]+$/, '/onboarding/contacts')
  const { toast } = useToast()
  const doc = ed.doc
  if (!doc) return null
  const order = doc.order ?? []
  const brandOptions = brands.order.map((b) => ({ value: b, label: brands.name(b) }))

  const add = () => {
    const name = newName.trim()
    const brandId = newBrand || brands.order[0]
    if (!name || !brandId) return
    let id = slugify(name)
    if (doc.items?.[id]) id = `${id}-${newId().slice(0, 4)}`
    const now = Date.now()
    const isClient = mode === 'client'
    const nameField = { ...emptyField(name, now), prefilled: !isClient, source: isClient ? 'client' : 'staff', confirmed: isClient } as const
    ed.setRaw(`items.${id}`, {
      brandId, name: nameField, address: emptyField('', now), phone: emptyField('', now), extra: emptyField('', now), timezone: 'America/New_York',
      schedules: emptyField<Schedule[]>([emptySchedule('regular', 'Regular hours')], now), createdAt: now,
    })
    ed.setRaw('order', [...order, id])
    setNewName('')
  }
  const remove = async (id: string) => {
    if (!(await confirm({ title: `Remove ${doc.items?.[id]?.name.value || 'this location'}?`, body: 'Its hours and details go with it.', confirmLabel: 'Remove' }))) return
    ed.remove(`items.${id}`)
    ed.setRaw('order', order.filter((x) => x !== id))
  }

  // Copy one schedule to other locations: it replaces a schedule with the same name there, or is added.
  const copySchedule = (ids: string[]) => {
    if (!copying) return
    for (const t of ids) {
      const cur = ed.getField<Schedule[]>(`items.${t}.schedules`)?.value ?? []
      const copy = structuredClone(copying.schedule)
      const at = cur.findIndex((s) => s.id === copy.id)
      ed.setValue(`items.${t}.schedules`, at >= 0 ? cur.map((s, i) => (i === at ? copy : s)) : [...cur, copy])
    }
    toast(`Copied ${copying.schedule.label} to ${ids.length === 1 ? (doc.items[ids[0]!]?.name.value ?? '1 location') : `${ids.length} locations`}`, 'success')
    setCopying(null)
  }

  return (
    <div className="grid gap-12">
      <CopyToDialog
        open={copying !== null}
        onClose={() => setCopying(null)}
        what={copying?.schedule.label ?? 'hours'}
        targets={order
          .filter((t) => t !== copying?.from && doc.items[t])
          .map((t) => ({
            id: t,
            label: doc.items[t]!.name.value || 'Untitled location',
            note: (ed.getField<Schedule[]>(`items.${t}.schedules`)?.value ?? []).some((s) => s.id === copying?.schedule.id) ? `Replaces its ${copying?.schedule.label}` : undefined,
          }))}
        onCopy={copySchedule}
      />
      {order.length === 0 && <NoEntities what="locations" hint={clientView ? 'Your Sonder contact will add them.' : 'Add each address below.'} />}
      {order.map((id, i) => {
        const loc = doc.items[id]
        if (!loc) return null
        const sched = ed.getField<Schedule[]>(`items.${id}.schedules`)
        const schedReview = clientView ? reviewInfo(sched, nameOf, { onConfirm: (v) => ed.confirm(`items.${id}.schedules`, v), onUndo: () => ed.undo(`items.${id}.schedules`) }) : undefined
        const upcoming = Boolean(loc.upcoming)
        return (
          <EntityCard key={id} eyebrow={`Location ${i + 1} · ${brands.name(loc.brandId)}${upcoming ? ' · Not open yet' : ''}`} title={loc.name.value || 'Untitled location'} onRemove={mode === 'prep' ? () => void remove(id) : undefined}>
            {mode === 'prep' && brands.order.length > 1 && (
              <Field label="Brand">
                <Select value={loc.brandId} onValueChange={(v) => ed.setRaw(`items.${id}.brandId`, v)} options={brandOptions} />
              </Field>
            )}
            <FieldEditor editor={ed} path={`items.${id}.name`} def={F.name!} />
            <FieldEditor editor={ed} path={`items.${id}.address`} def={F.address!} rows={2} />
            <MainLine contacts={Object.entries(contacts.data?.items ?? {}).filter(([, c]) => c.topics?.includes('phone') && contactCovers(c, id))} href={contactsHref} brandId={loc.brandId} />
            {mode === 'prep' && (
              <Toggle
                size="sm"
                checked={upcoming}
                onChange={(v) => ed.setRaw(`items.${id}.upcoming`, v)}
                label="Not open yet"
                description="Announced but not open. Hours aren’t needed until it opens; add the expected opening instead."
              />
            )}
            {upcoming && <FieldEditor editor={ed} path={`items.${id}.opening`} def={{ ...F.opening!, required: true }} />}
            <div className="grid gap-4" data-field={`items.${id}.schedules`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">
                  Hours
                  {upcoming && <span className="ml-2 font-normal text-dim normal-case tracking-normal">Optional until it opens</span>}
                </span>
                {schedReview && <ReviewTag state={schedReview.state} />}
              </div>
              <ScheduleBuilder
                value={sched?.value ?? []}
                onChange={(v) => ed.setValue(`items.${id}.schedules`, v)}
                onCopy={order.length > 1 ? (schedule) => setCopying({ from: id, schedule }) : undefined}
              />
              {schedReview && <ReviewBar review={schedReview} />}
            </div>
            <FieldEditor editor={ed} path={`items.${id}.extra`} def={F.extra!} rows={2} />
          </EntityCard>
        )
      })}
      {mode !== 'preview' && brands.order.length > 0 && (
        <AddRow label="Add location" onAdd={add}>
          <Field label="New location name" labelHidden className="min-w-64">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Location name" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} />
          </Field>
          {brands.order.length > 1 && (
            <Field label="Brand" labelHidden className="min-w-48">
              <Select value={newBrand || brands.order[0]} onValueChange={setNewBrand} options={brandOptions} />
            </Field>
          )}
        </AddRow>
      )}
    </div>
  )
}

/** A location's main line comes from Contacts (tagged "Main line"), so a number is only ever kept in one place. */
function MainLine({ contacts, href, brandId }: { contacts: [string, ContactsSection['items'][string]][]; href: string; brandId: string }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">Main line</span>
      {contacts.length === 0 ? (
        <p className="text-small text-dim">
          None yet.{' '}
          <Link to={href} state={{ focusGroup: brandId } satisfies ContactsNavState} className="text-ink underline underline-offset-4">
            Add it in Contact information
          </Link>
        </p>
      ) : (
        <ul className="grid gap-1 text-body">
          {contacts.map(([cid, c]) => (
            <li key={cid} className="flex flex-wrap items-baseline gap-x-3">
              {c.phone?.value ? <a href={`tel:${c.phone.value.replace(/[^\d+]/g, '')}`} className="text-ink tabular-nums">{c.phone.value}</a> : <span className="text-dim">No number yet</span>}
              {c.name?.value?.trim() && <span className="text-small text-dim">{contactLabel(c)}</span>}
              <Link to={href} state={{ focusContact: cid }} className="text-small text-dim underline underline-offset-4 hover:text-ink">
                Edit in Contact information
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
